import { useEffect, useState } from "react";
import { GraduationCap, Brain, Bell, CheckCircle2, AlertTriangle, BookOpenCheck } from "lucide-react";
import { api } from "../../lib/api";
import LearningStyleBadge from "../../components/LearningStyleBadge";
import CoinBadge from "../../components/CoinBadge";
import { useRealtime } from "../../context/RealtimeContext";

export default function Dashboard() {
    const [data, setData] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const { lastNotification, notifUnread, markAllNotifRead } = useRealtime();

    async function load() {
        const [r, n] = await Promise.all([
            api.get("/dashboards/me"),
            api.get("/notifications", { params: { unread_only: false } }),
        ]);
        setData(r.data);
        setNotifications(n.data || []);
    }
    useEffect(() => {
        load();
    }, []);

    useEffect(() => {
        if (lastNotification) {
            api.get("/notifications").then((r) => setNotifications(r.data || [])).catch(() => {});
        }
    }, [lastNotification]);

    async function clearNotifications() {
        await markAllNotifRead();
        setNotifications((rows) => rows.map((r) => ({ ...r, read: true })));
    }

    if (!data) return <div className="p-6 text-edu-muted">Cargando…</div>;
    const { user, child, recent_exams, transactions } = data;

    if (!child)
        return (
            <div className="p-6 md:p-10">
                <h1 className="text-3xl font-semibold mb-2">¡Bienvenido/a {user.name}! 👋</h1>
                <p className="text-edu-body">No tienes un hijo/a asociado. Contacta al administrador.</p>
            </div>
        );

    return (
        <div className="p-6 md:p-10 fade-in" data-testid="padre-dashboard">
            <h1 className="text-3xl font-semibold mb-1">¡Bienvenido/a {user.name}! 👋</h1>
            <p className="text-edu-body mb-7">Información académica de {child.name}.</p>

            {notifications.length > 0 && (
                <div className="edu-card p-5 mb-6" data-testid="parent-notifications">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold inline-flex items-center gap-2">
                            <Bell size={18} className="text-edu-navy" />
                            Notificaciones
                            {notifUnread > 0 && (
                                <span className="bg-edu-gold text-[#0A0A0A] text-xs font-bold rounded-full px-2 py-0.5">
                                    {notifUnread}
                                </span>
                            )}
                        </h3>
                        {notifUnread > 0 && (
                            <button
                                onClick={clearNotifications}
                                className="text-xs text-edu-navy hover:underline"
                                data-testid="clear-notifications"
                            >
                                Marcar todas como leídas
                            </button>
                        )}
                    </div>
                    <div className="space-y-2 max-h-60 overflow-y-auto edu-scroll">
                        {notifications.slice(0, 10).map((n) => {
                            const p = n.payload || {};
                            const ok = p.grade === "Aprobado";
                            const mid = p.grade === "Intermedio";
                            const Icon = ok ? CheckCircle2 : mid ? BookOpenCheck : AlertTriangle;
                            const color = ok ? "text-emerald-600" : mid ? "text-yellow-600" : "text-red-600";
                            return (
                                <div
                                    key={n.id}
                                    className={`flex items-start gap-3 p-3 rounded-xl ${
                                        n.read ? "bg-[#F8F9FB]" : "bg-edu-gold-soft border border-edu-gold/30"
                                    }`}
                                    data-testid={`notif-${n.id}`}
                                >
                                    <Icon size={16} className={`${color} mt-0.5 flex-shrink-0`} />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium">
                                            {p.child_name} {ok ? "aprobó" : mid ? "obtuvo nota intermedia en" : "desaprobó"}{" "}
                                            <span className="font-semibold">{p.exam_title}</span>
                                        </div>
                                        <div className="text-xs text-edu-muted">
                                            {p.subject} · {p.score_percent}% ({p.grade}) ·{" "}
                                            {(p.submitted_at || n.created_at || "").slice(0, 10)}
                                        </div>
                                    </div>
                                    {!n.read && (
                                        <span className="bg-edu-gold w-2 h-2 rounded-full flex-shrink-0 mt-2" />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="grid md:grid-cols-2 gap-5 mb-7">
                <div className="edu-card p-6">
                    <GraduationCap size={22} className="text-edu-navy mb-2" />
                    <div className="text-xs uppercase tracking-widest text-edu-muted font-semibold">Hijo/a</div>
                    <div className="text-xl font-semibold">{child.name}</div>
                    <div className="text-xs text-edu-muted">{child.grade}</div>
                </div>
                <div className="edu-card p-6">
                    <Brain size={22} className="text-edu-navy mb-2" />
                    <div className="text-xs uppercase tracking-widest text-edu-muted font-semibold">
                        Estilo de Aprendizaje
                    </div>
                    <div className="mt-2">
                        <LearningStyleBadge style={child.learning_style} size="lg" />
                    </div>
                </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <div className="edu-card p-6">
                    <h3 className="text-lg font-semibold mb-3">Exámenes recientes</h3>
                    <div className="space-y-2">
                        {recent_exams.length === 0 && (
                            <p className="text-sm text-edu-muted">Sin exámenes recientes.</p>
                        )}
                        {recent_exams.map((e, i) => (
                            <div
                                key={i}
                                className="flex items-center justify-between p-3 bg-[#F8F9FB] rounded-xl"
                            >
                                <div>
                                    <div className="font-medium text-sm">{e.exam_title}</div>
                                    <div className="text-xs text-edu-muted">
                                        {e.subject} · {(e.submitted_at || "").slice(0, 10)}
                                    </div>
                                </div>
                                <div className="text-lg font-semibold">{e.score_percent}%</div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="edu-card p-6">
                    <h3 className="text-lg font-semibold mb-3">Historial de monedas</h3>
                    <div className="space-y-2">
                        {transactions.length === 0 && (
                            <p className="text-sm text-edu-muted">Sin movimientos.</p>
                        )}
                        {transactions.map((t, i) => (
                            <div
                                key={i}
                                className="flex items-center justify-between p-3 bg-[#F8F9FB] rounded-xl"
                            >
                                <div>
                                    <div className="text-sm font-medium">{t.concept}</div>
                                    <div className="text-xs text-edu-muted">
                                        {(t.created_at || "").slice(0, 10)}
                                    </div>
                                </div>
                                <CoinBadge amount={t.amount} size="sm" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
